import { SimpleSpine } from '@/containers/common/SimpleSpine'
import { ReelsContainerHelper } from '@/containers/reels/ReelsContainerHelper'
import { IStoppingReelsData, Orientation } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import * as PIXI from 'pixi.js'
import gsap from 'gsap'
import Facade from '@/Facade'
import { ContainersNames } from '@/containers/ContainersNames'
import GeneralSignals from '@/signals/GeneralSignals'

export class AnticipationContainer extends PIXI.Container {
    /** Nudge anticipation spine slightly above reel center. */
    private static readonly SPINE_Y_OFFSET = 10

    private readonly _anticipationSpine: SimpleSpine | null
    private readonly _frameWrapper: PIXI.Container
    private _anticipationIndices: number[]
    private _isActive: number[]
    private _isReelsStopped: boolean
    private _debugForcedReelIndex: number | null
    private readonly _onKeyDown: (event: KeyboardEvent) => void

    public constructor() {
        super()
        GameSignals.stoppingReels.connect(this.onStoppingReels)
        GameSignals.reelStopped.connect(this.onSpinReelStopped)
        GameSignals.stopButtonClicked.connect(this.onImmediateStopReels)
        GeneralSignals.orientationChanged.connect(this.onOrientationChanged)

        try {
            this._anticipationSpine = new SimpleSpine('anticipation')
            this._anticipationSpine.position.set(
                Facade.symbolsConfig.WIDTH / 2,
                ReelsContainerHelper.calculateContainerHeight() / 2 - AnticipationContainer.SPINE_Y_OFFSET
            )
            this.applyScaleForOrientation(Facade.orientation)
        } catch (error) {
            // Anticipation spine not available: keep silent fail-safe.
            this._anticipationSpine = null
        }

        this._frameWrapper = new PIXI.Container()
        this._frameWrapper.name = ContainersNames.ANIMATION_WRAPPER
        if (this._anticipationSpine) {
            this._frameWrapper.addChild(this._anticipationSpine)
        }
        this.addChild(this._frameWrapper)

        this.visible = false
        this.alpha = 0
        this._anticipationIndices = null
        this._isActive = []
        this._debugForcedReelIndex = null

        this._onKeyDown = (event: KeyboardEvent) => {
            const key = event.code || event.key
            if (key === 'Digit3' || key === 'Numpad3' || key === '3') {
                this.toggleDebugAnticipation(2)
                return
            }

            if (key === 'Digit4' || key === 'Numpad4' || key === '4') {
                this.toggleDebugAnticipation(3)
                return
            }

            if (key === 'Digit5' || key === 'Numpad5' || key === '5') {
                this.toggleDebugAnticipation(4)
            }
        }

        window.addEventListener('keydown', this._onKeyDown)
    }

    private onImmediateStopReels = () => {
        this._isReelsStopped = true
        if (this._isActive.length) {
            this.hideFrame(true)
        }
    }

    private onStoppingReels = (data: IStoppingReelsData) => {
        if (data.isHoldAndWinRespin) {
            this._anticipationIndices = []
            this._isReelsStopped = true
            this.hideFrame()
            return
        }

        this._anticipationIndices = data.anticipationIndices
        this._isReelsStopped = false
    }

    private onSpinReelStopped = (reelIdx: number) => {
        if (this._debugForcedReelIndex != null) {
            return
        }

        const nextReelIdx: number = reelIdx + 1
        const canShow: boolean =
            this._anticipationIndices &&
            this._anticipationIndices.includes(nextReelIdx) &&
            !this._isReelsStopped &&
            !this._isActive.includes(nextReelIdx)

        if (canShow) {
            this.showFrame(nextReelIdx)
            return
        }

        if (this._isActive.length) {
            this.hideFrame()
        }
    }

    private showFrame(reelIdx: number) {
        this._frameWrapper.position.x = ReelsContainerHelper.calculateReelWidth() * reelIdx
        if (this._anticipationSpine) {
            this._anticipationSpine.play('win_loop', true)
        }
        this.visible = true
        GameSignals.showSuspenseAnimation.emit()

        if (!this._isActive.length) {
            gsap.to(this, { duration: 0.3, alpha: 1 })
        }

        this._isActive = [reelIdx]
    }

    private hideFrame(immediately: boolean = false) {
        this._isActive = []

        gsap.killTweensOf(this)
        if (immediately) {
            this.alpha = 0
            if (this._anticipationSpine) {
                this._anticipationSpine.stop()
            }
            this.visible = false
            GameSignals.hideSuspenseAnimation.emit()
            return
        }

        gsap.to(this, {
            duration: 0.5,
            alpha: 0,
            onComplete: () => {
                if (this._anticipationSpine) {
                    this._anticipationSpine.stop()
                }
                this.visible = false
                GameSignals.hideSuspenseAnimation.emit()
            },
        })
    }

    private toggleDebugAnticipation(reelIndex: number): void {
        if (this._debugForcedReelIndex === reelIndex) {
            this._debugForcedReelIndex = null
            this.hideFrame()
            return
        }

        this._debugForcedReelIndex = reelIndex
        this.showFrame(reelIndex)
    }

    private onOrientationChanged = (orientation: string): void => {
        this.applyScaleForOrientation(orientation)
    }

    private applyScaleForOrientation(orientation: string): void {
        if (!this._anticipationSpine) {
            return
        }

        if (orientation === Orientation.LANDSCAPE) {
            this._anticipationSpine.scale.set(1.775, 1.6)
            return
        } else {
            this._anticipationSpine.scale.set(1.775, 1.6)
            return
        }

        //this._anticipationSpine.scale.set(0.85)
    }

    public override destroy(options?: PIXI.DestroyOptions): void {
        window.removeEventListener('keydown', this._onKeyDown)
        super.destroy(options)
    }
}
